/**
 * /admin/roadmap — Internal staged-roadmap dashboard (Brief B)
 *
 * Read-only status + admin levers (tier, INDEX, compliance) for the
 * field-captured plants. Mirrors the rails laid down in server.py.
 * Auth-gated (ProtectedRoute requireAdmin in App.js).
 */
import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, AlertTriangle, Lock, Unlock, Sparkles } from "lucide-react";
import api from "../api";

const TIER_OPTIONS = ["free", "pro", "premium"];

export default function AdminRoadmap() {
  const [roadmap, setRoadmap] = useState(null);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [savingSlug, setSavingSlug] = useState(null);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const [r, p] = await Promise.all([
        api.get("/roadmap"),
        api.get("/admin/plants"),
      ]);
      setRoadmap(r.data);
      setPlants(p.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function setTier(slug, tier) {
    setSavingSlug(slug);
    try {
      await api.post(`/admin/plants/${slug}/tier`, { tier });
      await load();
    } finally { setSavingSlug(null); }
  }
  async function toggleIndex(slug, current) {
    setSavingSlug(slug);
    try {
      await api.post(`/admin/plants/${slug}/index-member`, { index_member: !current });
      await load();
    } finally { setSavingSlug(null); }
  }
  async function recompute(slug) {
    setSavingSlug(slug);
    try {
      await api.post(`/admin/plants/${slug}/recompute-strength`);
      await load();
    } finally { setSavingSlug(null); }
  }
  async function runComplianceScan() {
    setScanning(true); setScanResult(null);
    try {
      const { data } = await api.post("/admin/compliance/scan");
      setScanResult(data);
    } catch (e) {
      setScanResult({ error: e.response?.data?.detail || e.message });
    } finally { setScanning(false); }
  }

  return (
    <div className="container-cd py-12" data-testid="admin-roadmap-page">
      <div className="overline">ADMIN · CORRIDOR · ROADMAP</div>
      <h1 className="font-display text-5xl mt-3 leading-[0.9]">Staged roadmap</h1>
      <p className="mt-4 text-sm max-w-2xl" style={{ color: "var(--ink-2)" }}>
        Plant count drives the stage gate. Features below the current stage are unlocked.
        Features above are gated rails — flip them on by hand or wait for the plant-count milestone.
      </p>

      {err && <div className="mt-4 cd-card p-4" style={{ color: "#dc2626" }}>{err}</div>}

      {loading ? (
        <div className="mt-10 overline">LOADING…</div>
      ) : (
        <>
          {/* STAGE STATUS */}
          <section className="mt-8 grid md:grid-cols-3 gap-4" data-testid="roadmap-stage-status">
            <div className="cd-card p-6">
              <div className="overline">CURRENT STAGE</div>
              <div className="font-display text-5xl mt-2" data-testid="roadmap-current-stage">
                {roadmap.stage}
              </div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                {roadmap.stage_name}
              </div>
            </div>
            <div className="cd-card p-6">
              <div className="overline">PLANT COUNT</div>
              <div className="font-display text-5xl mt-2" data-testid="roadmap-plant-count">
                {roadmap.plant_count}
              </div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                published manufacturers + field signups
              </div>
            </div>
            <div className="cd-card p-6">
              <div className="overline">NEXT STAGE</div>
              <div className="font-display text-5xl mt-2" data-testid="roadmap-next-stage">
                {roadmap.next_stage ?? "—"}
              </div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                {roadmap.next_stage_name
                  ? `${roadmap.plants_to_next_stage} plants away`
                  : "Market leader. No higher stage."}
              </div>
            </div>
          </section>

          {/* STAGE LADDER */}
          <section className="mt-8" data-testid="roadmap-stage-ladder">
            <div className="overline mb-3">STAGE LADDER</div>
            <div className="space-y-2">
              {roadmap.stages.map((s) => {
                const isUnlocked = roadmap.plant_count >= s.min;
                const isCurrent = roadmap.stage === s.stage;
                return (
                  <div
                    key={s.stage}
                    className="cd-card p-4 grid md:grid-cols-12 gap-4 items-center"
                    style={{
                      borderColor: isCurrent ? "var(--orange)" : "var(--line)",
                      background: isCurrent ? "rgba(255,107,0,0.04)" : "white",
                    }}
                    data-testid={`roadmap-stage-${s.stage}`}
                  >
                    <div className="md:col-span-2 flex items-center gap-3">
                      {isUnlocked ? (
                        <Unlock size={16} style={{ color: "var(--green)" }} />
                      ) : (
                        <Lock size={16} style={{ color: "var(--ink-2)" }} />
                      )}
                      <div>
                        <div className="font-display text-2xl">Stage {s.stage}</div>
                        <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                          ≥ {s.min} plants
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-3 font-heading font-semibold">{s.name}</div>
                    <div className="md:col-span-7 flex flex-wrap gap-1.5">
                      {s.features.map((f) => (
                        <span
                          key={f}
                          className="cd-chip"
                          style={{
                            opacity: isUnlocked ? 1 : 0.4,
                            fontSize: 10,
                          }}
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* COMPLIANCE SCAN */}
          <section
            className="mt-10 cd-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            data-testid="roadmap-compliance-panel"
          >
            <div>
              <div className="overline" style={{ color: "var(--orange)" }}>
                <AlertTriangle size={11} className="inline mr-2 -mt-1" /> IMMEX EXPIRATION SCANNER
              </div>
              <p className="text-sm mt-2" style={{ color: "var(--ink-2)", maxWidth: 560 }}>
                Scans field-captured plants for IMMEX certs expiring within 30 days.
                Fires <code>event: compliance_alert</code> webhook to Make.com per plant. 7-day dedupe.
                Schedule a daily Make.com cron to call this endpoint.
              </p>
              {scanResult && (
                <div className="mt-3 font-mono text-xs" data-testid="roadmap-scan-result">
                  {scanResult.error ? (
                    <span style={{ color: "#dc2626" }}>Error: {scanResult.error}</span>
                  ) : (
                    <span>Fired {scanResult.fired} alerts · Deduped {scanResult.skipped_dedupe}</span>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={runComplianceScan}
              disabled={scanning}
              className="btn btn-primary"
              data-testid="roadmap-compliance-scan-btn"
            >
              {scanning ? <><Loader2 size={14} className="animate-spin" /> Scanning…</> : "Run compliance scan"}
            </button>
          </section>

          {/* FIELD PLANTS TABLE */}
          <section className="mt-10" data-testid="roadmap-plants-section">
            <div className="overline mb-3">FIELD-CAPTURED PLANTS · {plants.length}</div>
            <div className="cd-card p-0 overflow-x-auto">
              <table className="cd-table" data-testid="roadmap-plants-table">
                <thead>
                  <tr>
                    <th>Plant</th>
                    <th>Park</th>
                    <th>Tier</th>
                    <th>INDEX</th>
                    <th>Strength</th>
                    <th>IMMEX cert</th>
                    <th>Avg resp</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {plants.map((p) => {
                    const expiry = p.immex_cert_expiry_date
                      ? new Date(p.immex_cert_expiry_date).toISOString().slice(0, 10)
                      : "—";
                    const isSaving = savingSlug === p.slug;
                    return (
                      <tr key={p.slug} data-testid={`roadmap-plant-row-${p.slug}`}>
                        <td className="font-semibold">{p.plant_name}</td>
                        <td className="font-mono text-xs">{p.industrial_park || "—"}</td>
                        <td>
                          <select
                            value={p.tier}
                            disabled={isSaving}
                            onChange={(e) => setTier(p.slug, e.target.value)}
                            className="cd-input"
                            style={{ padding: "4px 8px", fontSize: 12 }}
                            data-testid={`roadmap-tier-${p.slug}`}
                          >
                            {TIER_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            onClick={() => toggleIndex(p.slug, p.index_member)}
                            disabled={isSaving}
                            className={p.index_member ? "cd-chip cd-chip-green" : "cd-chip"}
                            data-testid={`roadmap-index-${p.slug}`}
                          >
                            {p.index_member ? "✓ INDEX" : "OFF"}
                          </button>
                        </td>
                        <td className="font-mono text-xs">
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span data-testid={`roadmap-strength-${p.slug}`}>{p.profile_strength}%</span>
                            <div style={{ width: 40, height: 4, background: "var(--bg)", borderRadius: 2, overflow: "hidden" }}>
                              <div
                                style={{
                                  width: `${p.profile_strength}%`,
                                  height: "100%",
                                  background:
                                    p.profile_strength >= 80 ? "var(--green)" :
                                    p.profile_strength >= 40 ? "var(--orange)" :
                                    "var(--ink-2)",
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="font-mono text-xs">
                          {p.immex_cert_number ? (
                            <span title={`Expires ${expiry}`}>
                              <ShieldCheck size={11} className="inline mr-1" />
                              {p.immex_cert_number} <span style={{ color: "var(--ink-2)" }}>· {expiry}</span>
                            </span>
                          ) : "—"}
                        </td>
                        <td className="font-mono text-xs">
                          {p.avg_response_hours != null ? `${p.avg_response_hours}h` : "—"}
                        </td>
                        <td>
                          <button
                            onClick={() => recompute(p.slug)}
                            disabled={isSaving}
                            className="cd-chip"
                            title="Recompute profile_strength"
                            data-testid={`roadmap-recompute-${p.slug}`}
                          >
                            <Sparkles size={11} /> Recalc
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
